/**
 * Presence Service - Tracks who is currently viewing a session
 * 
 * This is a client-side implementation using localStorage to track presence
 * across browser tabs. It can be upgraded to use a backend API in the future.
 */

export interface PresenceParticipant {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  lastSeenUtc: string;
  sessionId: string;
}

interface PresenceRecord {
  [sessionId: string]: {
    [userId: string]: PresenceParticipant;
  };
}

const PRESENCE_STORAGE_KEY = 'intakeai_presence';
const PRESENCE_EXPIRY_MS = 30000; // 30 seconds - consider someone gone if no heartbeat

/**
 * Get all presence records from localStorage
 */
const getPresenceRecords = (): PresenceRecord => {
  try {
    const stored = localStorage.getItem(PRESENCE_STORAGE_KEY);
    if (!stored) return {};
    return JSON.parse(stored) as PresenceRecord;
  } catch (error) {
    console.error('Error reading presence records:', error);
    return {};
  }
};

/**
 * Save presence records to localStorage
 */
const savePresenceRecords = (records: PresenceRecord): void => {
  try {
    localStorage.setItem(PRESENCE_STORAGE_KEY, JSON.stringify(records));
    // Trigger storage event for other tabs
    window.dispatchEvent(new Event('storage'));
  } catch (error) {
    console.error('Error saving presence records:', error);
  }
};

/**
 * Clean up expired presence records
 */
const cleanupExpiredPresence = (records: PresenceRecord): PresenceRecord => {
  const now = Date.now();
  const cleaned: PresenceRecord = {};

  for (const [sessionId, participants] of Object.entries(records)) {
    cleaned[sessionId] = {};
    for (const [userId, participant] of Object.entries(participants)) {
      const lastSeen = new Date(participant.lastSeenUtc).getTime();
      if (now - lastSeen < PRESENCE_EXPIRY_MS) {
        cleaned[sessionId][userId] = participant;
      }
    }
    // Remove empty sessions
    if (Object.keys(cleaned[sessionId]).length === 0) {
      delete cleaned[sessionId];
    }
  }

  return cleaned;
};

/**
 * Update presence for the current user in a session
 */
const updatePresence = (
  sessionId: string,
  userId: string,
  displayName: string,
  avatarUrl: string | null
): void => {
  const records = getPresenceRecords();
  const cleaned = cleanupExpiredPresence(records);

  if (!cleaned[sessionId]) {
    cleaned[sessionId] = {};
  }

  cleaned[sessionId][userId] = {
    userId,
    displayName,
    avatarUrl,
    lastSeenUtc: new Date().toISOString(),
    sessionId
  };

  savePresenceRecords(cleaned);
};

/**
 * Remove presence for the current user from a session
 */
const removePresence = (sessionId: string, userId: string): void => {
  const records = getPresenceRecords();
  
  if (records[sessionId]?.[userId]) {
    delete records[sessionId][userId];
    
    // Clean up empty session
    if (Object.keys(records[sessionId]).length === 0) {
      delete records[sessionId];
    }
    
    savePresenceRecords(records);
  }
};

/**
 * Get all participants for a session
 */
const getSessionParticipants = (sessionId: string): PresenceParticipant[] => {
  const records = getPresenceRecords();
  const cleaned = cleanupExpiredPresence(records);
  
  if (!cleaned[sessionId]) {
    return [];
  }

  return Object.values(cleaned[sessionId]);
};

/**
 * Get current user information from the DOM/context
 * This is a helper to extract user info without requiring Auth0 context
 */
const getCurrentUserInfo = (): { userId: string; displayName: string; avatarUrl: string | null } | null => {
  try {
    // Try to get user info from sessionStorage (set by AppUserProvider)
    const userInfo = sessionStorage.getItem('intakeai_current_user');
    if (userInfo) {
      return JSON.parse(userInfo);
    }
    
    // Fallback: generate a temporary ID if no user info available
    // This allows the presence feature to work even without full integration
    const tempId = sessionStorage.getItem('intakeai_temp_user_id');
    if (tempId) {
      return {
        userId: tempId,
        displayName: 'Anonymous User',
        avatarUrl: null
      };
    }
    
    // Generate and store a temporary ID
    const newTempId = `temp_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('intakeai_temp_user_id', newTempId);
    return {
      userId: newTempId,
      displayName: 'Anonymous User',
      avatarUrl: null
    };
  } catch (error) {
    console.error('Error getting current user info:', error);
    return null;
  }
};

/**
 * Start the presence lifecycle for a session
 * 
 * @param sessionId - The session to track presence for
 * @param onParticipantsChange - Callback when participants change
 * @param heartbeatInterval - How often to send heartbeats (ms)
 * @returns Cleanup function to stop presence tracking
 */
export const startPresenceLifecycle = (
  sessionId: string,
  onParticipantsChange: (participants: PresenceParticipant[]) => void,
  heartbeatInterval: number = 10000
): (() => void) => {
  // Get current user info
  const userInfo = getCurrentUserInfo();
  
  if (!userInfo) {
    console.warn('No user info available for presence tracking');
    // Return a no-op cleanup function
    return () => {};
  }

  const { userId, displayName, avatarUrl } = userInfo;

  // Initial presence update
  updatePresence(sessionId, userId, displayName, avatarUrl);
  
  // Notify initial state
  const initialParticipants = getSessionParticipants(sessionId);
  onParticipantsChange(initialParticipants);

  // Set up heartbeat interval
  const heartbeatTimer = setInterval(() => {
    updatePresence(sessionId, userId, displayName, avatarUrl);
    const participants = getSessionParticipants(sessionId);
    onParticipantsChange(participants);
  }, heartbeatInterval);

  // Listen for storage changes (from other tabs)
  const handleStorageChange = () => {
    const participants = getSessionParticipants(sessionId);
    onParticipantsChange(participants);
  };

  window.addEventListener('storage', handleStorageChange);

  // Cleanup function
  return () => {
    clearInterval(heartbeatTimer);
    window.removeEventListener('storage', handleStorageChange);
    removePresence(sessionId, userId);
  };
};

/**
 * Helper function to set current user info in sessionStorage
 * Should be called by AppUserProvider when user is loaded
 */
export const setCurrentUserForPresence = (
  userId: string,
  displayName: string,
  avatarUrl: string | null
): void => {
  try {
    sessionStorage.setItem(
      'intakeai_current_user',
      JSON.stringify({ userId, displayName, avatarUrl })
    );
  } catch (error) {
    console.error('Error setting current user for presence:', error);
  }
};
